import { useNavigate, useParams } from 'react-router-dom';
import { PATHS } from 'src/actuators/router.tsx';
import { I18n } from 'src/modules/common/components/I18n/I18n.tsx';
import { MESSAGES } from 'src/modules/common/constants.ts';
import { Suspense, useCallback, useContext, useEffect, useMemo } from 'react';
import { ConfigContext } from 'src/configContext.ts';
import { useFilterLogic } from 'src/modules/common/hooks/useFilterLogic.ts';
import { skipToken } from '@apollo/client';
import { MainLayout } from 'src/modules/common/layouts/MainLayout.tsx';
import { NavigationContainer } from 'src/modules/common/components/Navigation/NavigationContainer.tsx';
import { NavigationButton } from 'src/modules/common/components/Navigation/NavigationButton.tsx';
import { BreadcrumbContainer } from 'src/common/Breadcrumb/BreadcrumbContainer.tsx';
import { useCheckMainFilter } from 'src/modules/common/hooks/useCheckMainFilter.ts';
import { initLocationState } from 'src/actuators/locationStateContext.ts';
import { RuntimeContext } from 'src/runtimeContext';
import { isRecommendedAccessory } from 'src/utils/isRecommendedAccessory';
import { Breadcrumbs } from 'src/actuators/Breadcrumbs.ts';
import { SkeletonPageLayout } from 'src/modules/common/skeletons/SkeletonPageLayout/SkeletonPageLayout';
import { NoData } from 'src/modules/common/components/NoData/NoData';
import { Accessory } from 'src/common/Accessory/Accessory';
import { Query } from 'src/modules/common/components/Query/Query';
import {
    ActuatorAccessoriesPageData,
    ActuatorAccessoriesPageVariables,
    getActuatorAccessoriesPageConfig,
} from 'src/actuators/accessories/ActuatorAccessoriesPageMapping.ts';
import { useLocationState } from 'src/actuators/user-input/useLocationState.ts';
import { LocationState } from 'src/actuators/LocationState.ts';
import { useReadUserInput } from 'src/actuators/user-input/useReadUserInput.ts';
import { MainLayoutHeader } from 'src/modules/common/components/MainLayoutHeader/MainLayoutHeader';
import { composeFilter, FilterConfig } from 'src/utils/composeFilter.ts';
import { ActuatorTooltipValue } from 'src/actuators/common/ActuatorTooltipValue/ActuatorTooltipValue';
import { AccessoryGrid } from 'src/common/Accessory/AccessoryGrid';
import { ActuatorBreadcrumbValue } from 'src/actuators/common/ActuatorBreadcrumbValue/ActuatorBreadcrumbValue';
import { detectActuatorType } from 'src/actuators/detectActuatorType';
import { ActuatorTypes } from 'src/actuators/user-input/calculations/constants.ts';
import { CheckboxButton } from 'src/modules/common/components/Checkbox/CheckboxButton.tsx';
import { AccessoriesSidebarComponent } from 'src/actuators/accessories/AccessoriesSidebarComponent.tsx';
import clsx from 'clsx';
import { useGetHomeBreadcrumb } from 'src/actuators/home/useGetHomeBreadcrumb.ts';

const categories = [
    { value: '581' },
    { value: '582' },
    { value: '6311' },
];

const filterName = 'ac';

export const ActuatorAccessoriesPage = () => {
    const {
        actuatorLabel,
        page,
    } = useParams();

    if (!actuatorLabel) {
        throw new Error('No actuator selected');
    }
    if (!page) {
        throw new Error('No page provided');
    }

    const [locationState, setLocationState] = useLocationState();
    const { isMobile } = useContext(RuntimeContext);
    useEffect(() => {
        if (locationState.actuatorLabel !== actuatorLabel) {
            setLocationState({ ...locationState, actuatorLabel });
        }
    }, [locationState, setLocationState, actuatorLabel, page]);

    const { userSettings, mandatoryFilterConfig } = useContext(ConfigContext);

    const pageConfig = useMemo(() => getActuatorAccessoriesPageConfig(userSettings.currentRegion), [userSettings]);
    if (pageConfig === null) {
        throw new Error('No Page Config!');
    }

    const onChange = useCallback((label: string) => {
        let selectedAccessories: LocationState['selectedAccessories'] = [];
        if (locationState.selectedAccessories.find((item) => item.label === label) !== undefined) {
            selectedAccessories = locationState.selectedAccessories.filter((item) => item.label !== label);
        } else {
            selectedAccessories = [...locationState.selectedAccessories, { label, qty: 1 }];
        }
        setLocationState({
            ...locationState,
            selectedAccessories,
        });
    }, [locationState, setLocationState]);

    const [
        searchParams,
        filter,
        checkboxHandler,
    ] = useFilterLogic([
        ...pageConfig.mainFilterConfig,
        ...pageConfig.additionalFilterConfig,
        ...[[filterName, filterName]] as FilterConfig,
    ]);

    const navigate = useNavigate();

    const homePageValue = useGetHomeBreadcrumb({ filter });

    useCheckMainFilter({
        searchParams,
        filter,
        navigate,
        filterConfig: [],
    });

    const { state } = useReadUserInput(searchParams, userSettings.currentRegion);

    const isLinear = useMemo(
        () => detectActuatorType(filter, navigate) === ActuatorTypes.Linear,
        [filter, navigate],
    );

    const breadcrumbsProps: Breadcrumbs = [
        {
            title: MESSAGES.SelectActuatorType,
            value: homePageValue,
            state: { ...initLocationState },
            to: {
                pathname: PATHS.Home,
                search: searchParams.toString(),
            },
        },
        {
            title: MESSAGES.ActuatorParams,
            value: <ActuatorBreadcrumbValue
                region={userSettings.currentRegion}
                state={state}
                currentLocale={userSettings.currentLocale}
                isLinear={isLinear}
            />,
            tooltipValue: <ActuatorTooltipValue
                region={userSettings.currentRegion}
                state={state}
                isLinear={isLinear}
                currentLocale={userSettings.currentLocale}
            />,
            state: { ...initLocationState },
            to: {
                pathname: PATHS.UserInput,
                search: searchParams.toString(),
            },
        },
        {
            title: MESSAGES.SelectedActuator,
            value: locationState.actuatorLabel,
            state: locationState,
            to: {
                pathname: [
                    PATHS.Actuators,
                    locationState.actuatorPage,
                ].join('/'),
                search: searchParams.toString(),
            },
        },
        {
            title: MESSAGES.SelectedAccessories,
            selected: true,
            disabled: true,
            value: locationState.selectedAccessories.length > 0
                ? locationState.selectedAccessories.map(({ label }) => label).join(', ') : '-',
            actionShown: locationState.selectedAccessories.length > 0,
            onActionClick: () => {
                setLocationState({
                    ...locationState,
                    selectedAccessories: [],
                });
            },
            to: '#',
        },
    ];

    const navigation = (
        <NavigationContainer isMobile={isMobile}>
            <NavigationButton
                type="back"
                state={locationState}
                to={{
                    pathname: [
                        PATHS.Actuators,
                        page,
                    ].join('/'),
                    search: searchParams.toString(),
                }}
            >
                <I18n messageId={MESSAGES.back}/>
            </NavigationButton>

            <NavigationButton
                type="forward"
                state={locationState}
                to={{
                    pathname: [
                        PATHS.Checkout,
                        encodeURIComponent(actuatorLabel),
                        page,
                    ].join('/'),
                    search: searchParams.toString(),
                }}
            >
                <I18n messageId={MESSAGES.next}/>
            </NavigationButton>
        </NavigationContainer>
    );

    const variables = useMemo((): {
        variables: ActuatorAccessoriesPageVariables;
    } | typeof skipToken => {
        if (filter === null) {
            return skipToken;
        }
        return {
            variables: {
                filter: composeFilter(
                    mandatoryFilterConfig.config,
                    mandatoryFilterConfig.values,
                    userSettings.currentLocale,
                ),
                label: actuatorLabel,
                locale: userSettings.currentLocale,
            },
        };
    }, [
        mandatoryFilterConfig,
        filter,
        actuatorLabel,
        userSettings,
    ]);

    const accessoriesFilterValues = filter[filterName] || [];

    const isSelected = (
        { source }: ActuatorAccessoriesPageData['accessories']['accessories'][0],
    ) => {
        if (accessoriesFilterValues.length === 0) {
            return true;
        }
        return accessoriesFilterValues.includes(source.replace('_', ''));
    };

    const renderAccessory = (item: ActuatorAccessoriesPageData['accessories']['accessories'][0]['item']) => (
        <Accessory
            chip={isRecommendedAccessory(item.zOption)
                ? <I18n messageId={MESSAGES.recommended}/> : null}
            accessory={item}
            onSelect={() => onChange(item.Label)}
            selected={locationState.selectedAccessories.find(({ label }) => label === item.Label) !== undefined}
            key={`${item.Label}-${item.ID}`}
        />
    );

    return (
        <div className="vss-actuators">
            <MainLayout>
                <MainLayoutHeader
                    titleId={MESSAGES.accessoriesPageTitle}
                    navigation={isMobile ? null : navigation}
                    breadcrumbs={(
                        <BreadcrumbContainer
                            isMobile={isMobile}
                            breadcrumbs={breadcrumbsProps}
                            containerWrapperClass="vss-actuators-breadcrumbs"
                        />
                    )}
                />

                <Suspense fallback={<SkeletonPageLayout/>}>
                    <Query<ActuatorAccessoriesPageData, ActuatorAccessoriesPageVariables>
                        query={pageConfig.query}
                        variables={variables}
                        onResult={(data) => {
                            if (!data) {
                                return;
                            }
                            const { accessories } = data;
                            const total = (accessories.accessories.length || 0);
                            if (total === 0) {
                                navigate({
                                    pathname: [
                                        PATHS.Checkout,
                                        encodeURIComponent(actuatorLabel),
                                        page,
                                    ].join('/'),
                                    search: searchParams.toString(),
                                }, { state: locationState });
                            }
                        }}
                    >
                        {({ data }) => {
                            if (data === undefined) {
                                return (
                                    <SkeletonPageLayout/>
                                );
                            }

                            if (data.accessories.accessories.length === 0) {
                                return <NoData/>;
                            }

                            return (
                                <div className={clsx({
                                    'vss-actuator-accessories-page': true,
                                    'vss-actuator-accessories-page--mobile': isMobile,
                                })}
                                >
                                    <AccessoriesSidebarComponent
                                        filter={filter}
                                        config={[[filterName, filterName]]}
                                        checkboxHandler={checkboxHandler}
                                    >
                                        {categories.map(({ value }) => {
                                            const disabled = data.accessories.accessories
                                                .filter(({ source }) => source === `_${value}`).length === 0;
                                            return (
                                                <CheckboxButton
                                                    key={value}
                                                    id={value}
                                                    disabled={disabled}
                                                    checked={accessoriesFilterValues.includes(value)}
                                                    onChange={() => checkboxHandler({ name: filterName, value })}
                                                >
                                                    <I18n messageId={value}/>
                                                </CheckboxButton>
                                            );
                                        })}
                                    </AccessoriesSidebarComponent>
                                    {data.accessories.accessories &&
                                        (
                                            <AccessoryGrid title={MESSAGES.accessoriesPageActuatorTitle}>
                                                {data.accessories.accessories.filter(isSelected)
                                                    .sort((a, b) => `${a.source}${a.item.Label}`
                                                        .localeCompare(`${b.source}${b.item.Label}`))
                                                    .map(({ item }) => renderAccessory(item))}
                                            </AccessoryGrid>
                                        )}
                                </div>
                            );
                        }}
                    </Query>
                </Suspense>
            </MainLayout>

            {isMobile && navigation}
        </div>
    );
};
